import React, { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUserStore } from '../../store/user-store';
import { ProfileBadge } from '../profile-badge';
import { MoreButton } from '../more-button';
import { ServerSideDatatable } from '../serverside-datatable';
import { Sprite } from '../sprite';
import { Role } from '../../services/auth-service/types';
import StringUtils from '../../utils';
import AddDashboardUser from '../AddDashboardUser';

interface Props {}

export const CustomerList: React.FC<Props> = () => {
  const { currentPage, loadingUsers, users, listUsers, totalCount } =
    useUserStore();

  const navigate = useNavigate();

  const navigateToOrder = useCallback(
    (id: number) => {
      navigate(`/customers/${id}`);
    },
    [navigate],
  );

  const fieldOptions = useMemo(() => {
    const labels = Object.keys(Role).filter(
      role => role.toLowerCase() !== Role.SuperUser,
    );
    const values = Object.values(Role).filter(role => role !== Role.SuperUser);
    return labels.map((label, index) => ({
      label: StringUtils.capitalize(label),
      value: values[index],
    }));
  }, []);

  return (
    <ServerSideDatatable
      defaultOrderReference="updatedAt"
      searchBarPlaceholder="Search by customer name, email"
      columns={[
        {
          id: 'name',
          name: <h5 className="text-gray font-medium text-base">Name</h5>,
          selector: data => data.email,
          sortField: 'firstName',
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <div className="flex flex-row hover:underline">
              <div className="flex flex-col justify-center">
                <ProfileBadge
                  firstName={row.firstName}
                  lastName={row.lastName}
                />
              </div>
              <div className="flex flex-col justify-center px-4 py-[5px]">
                <Link
                  className="text-sm font-semibold text-blue hover:underline"
                  to={`/customers/${row.id}`}>
                  {row.firstName} {row.lastName}
                </Link>
                <p className="text-gray text-xs leading-[18px]">{row.email}</p>
              </div>
            </div>
          ),
          grow: 4,
          sortable: true,
        },
        {
          id: 'access',
          name: (
            <h5 className="text-gray font-medium text-base">
              Access / User type
            </h5>
          ),
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: (row: any) => (
            <div>
              {row.roles && row.roles.includes('superuser') && (
                <div className="px-1.5 py-[2px] flex flex-row">
                  <Sprite
                    name="icon-person-admin"
                    className="w-[20px] h-[20px] text-gray fill-gray"
                  />
                  <div className="px-[6px] py-[2px] text-gray fill-gray">
                    Superuser
                  </div>
                </div>
              )}
              {row.roles && row.roles.includes('admin') && (
                <div className="px-1.5 py-[2px] flex flex-row">
                  <Sprite
                    name="icon-person-admin"
                    className="w-[20px] h-[20px] text-gray fill-gray"
                  />
                  <div className="px-[6px] py-[2px] text-gray fill-gray">
                    Admin
                  </div>
                </div>
              )}
              {row.roles && row.roles.includes('user') && (
                <div className="px-1.5 py-[2px] flex flex-row">
                  <Sprite
                    name="icon-person"
                    className="w-[20px] h-[20px] text-gray fill-gray"
                  />
                  <div className="px-[6px] py-[2px] text-gray fill-gray">
                    Customer
                  </div>
                </div>
              )}
              {row.roles && row.roles.includes('physician') && (
                <div className="px-1.5 py-[2px] flex flex-row">
                  <Sprite
                    name="icon-report"
                    className="w-[20px] h-[20px] text-gray fill-gray"
                  />
                  <div className="px-[6px] py-[2px] text-gray fill-gray">
                    Physician
                  </div>
                </div>
              )}
              {row.roles && row.roles.includes('eventmanager') && (
                <div className="px-1.5 py-[2px] flex flex-row">
                  <Sprite
                    name="icon-admin"
                    className="w-[20px] h-[20px] text-gray fill-gray"
                  />
                  <div className="px-[6px] py-[2px] text-gray fill-gray">
                    Event Manager
                  </div>
                </div>
              )}
            </div>
          ),
          grow: 5,
          sortable: true,
          sortField: 'roles',
        },
        {
          id: 'registeredAt',
          name: (
            <h5 className="text-gray font-medium text-base">Registered At</h5>
          ),
          cell: (row: any) => (
            <div>
              {!row.registeredAt ? (
                <>{row.registeredAt}</>
              ) : row.registeredAt.includes('online') ? (
                <div className="px-1.5 py-[2px] flex flex-row">
                  <div className="px-[6px] py-[2px] text-gray fill-gray">
                    Default
                  </div>
                </div>
              ) : (
                <div className="px-1.5 py-[2px] flex flex-row">
                  <div className="px-[6px] py-[2px] text-gray fill-gray">
                    On-site
                  </div>
                </div>
              )}
            </div>
          ),
          grow: 4,
          sortable: true,
          sortField: 'registeredAt',
        },
        {
          id: 'actions',
          name: '',
          // eslint-disable-next-line react/no-unstable-nested-components
          cell: row => (
            <MoreButton>
              <button
                type="button"
                className="text-base hover:bg-blue-lighter hover:text-blue text-black font-medium w-full px-4 py-1 text-left"
                onClick={() => navigateToOrder(row.id)}>
                View details
              </button>
            </MoreButton>
          ),
          grow: 0,
        },
      ]}
      filterBy="roles"
      filterByOptions={fieldOptions}
      data={users}
      currentPage={currentPage}
      paginationTotalRows={totalCount}
      progressPending={loadingUsers}
      serverAction={listUsers}
      hideRefresh
      rightFilterComponent={<AddDashboardUser />}
    />
  );
};
